/**
 * Page carte : la France seule, le reste du monde masqué.
 *
 * La carte n'est construite qu'à la première visite de l'onglet. Leaflet est
 * lourd, et une personne qui ne consulte que le verdict n'a aucune raison
 * d'en payer le coût.
 */
import L from 'leaflet';
import {
  MAP_LABEL_MIN_ZOOM,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  WIND_MIN_KMH,
} from '../../core/constants';
import { FRANCE_BOX, FRANCE_RINGS } from '../../core/france';
import { dayLabel, directionTo, durationWords, formatClock } from '../../core/format';
import { distanceKm } from '../../core/geo';
import { windCorridor } from '../../core/plume';
import type { BurntArea, FireZone, PlumeAssessment, Ring } from '../../core/types';
import { regimeFor } from '../../core/wind';
import { isRealFire, isWeakDetection } from '../../core/zones';
import { plumeContext, selectNearbyFires } from '../../state/selectors';
import type { AppState } from '../../state/store';
import { html, render } from '../html';

const COLOR_FIRE = '#ff4d1f';
const COLOR_FIRE_HOT = '#ffb01f';
const COLOR_SMOKE = '#ff9b4d';
const COLOR_WIND = '#7dd3fc';
/** Surfaces déjà brûlées : brun de cendre, volontairement loin de tout rouge. */
const COLOR_BURNT = '#8a6a4f';

const toLatLngs = (ring: Ring): L.LatLngTuple[] => ring.map((point) => [point[0], point[1]]);

export interface MapPage {
  /** Construit la carte si besoin, puis recalcule sa taille. */
  activate(state: AppState): void;
  render(state: AppState): void;
  focusZone(state: AppState, zoneId: string): void;
}

export function createMapPage(
  container: HTMLElement,
  cardHost: HTMLElement,
): MapPage {
  let map: L.Map | null = null;
  let youMarker: L.Marker | null = null;
  let windLayers: L.Layer[] = [];
  let zoneLayers: L.Layer[] = [];
  let burntLayers: L.Layer[] = [];
  /** Dernière liste dessinée, pour ne pas retracer des centaines de sommets. */
  let burntDrawn: readonly BurntArea[] | null = null;
  let selectedId: string | null = null;
  let lastState: AppState | null = null;

  function build(state: AppState): L.Map {
    const instance = L.map(container, {
      zoomControl: true,
      attributionControl: true,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      maxBounds: L.latLngBounds(
        [FRANCE_BOX.south, FRANCE_BOX.west],
        [FRANCE_BOX.north, FRANCE_BOX.east],
      ),
      maxBoundsViscosity: 1,
    }).setView(
      [state.position.lat, state.position.lon],
      state.position.label === 'France' ? 6 : 10,
    );

    // OSM France : les noms de villes et de pays sont en français.
    L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
      subdomains: 'abc',
      noWrap: true,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      attribution:
        '&copy; <a href="https://www.openstreetmap.fr">OpenStreetMap France</a>',
    }).addTo(instance);

    /*
     * Masque : un rectangle couvrant le monde, percé de trous à la forme de la
     * France. Il vit dans sa propre couche, entre le fond de carte (200) et
     * les zones de feu (400), pour que le filtre sombre appliqué aux tuiles ne
     * touche ni le masque ni les polygones rouges.
     */
    instance.createPane('mask');
    const maskPane = instance.getPane('mask');
    if (maskPane) {
      maskPane.style.zIndex = '350';
      maskPane.style.pointerEvents = 'none';
    }

    if (FRANCE_RINGS.length > 0) {
      const world: L.LatLngTuple[] = [
        [-89, -179],
        [-89, 179],
        [89, 179],
        [89, -179],
      ];
      L.polygon([world, ...FRANCE_RINGS.map(toLatLngs)], {
        pane: 'mask',
        fillColor: '#0b0e13',
        fillOpacity: 0.94,
        fillRule: 'evenodd',
        color: '#0b0e13',
        weight: 0,
        interactive: false,
      }).addTo(instance);

      for (const ring of FRANCE_RINGS) {
        const points = toLatLngs(ring);
        L.polyline([...points, points[0]!], {
          pane: 'mask',
          color: '#3d4855',
          weight: 1.2,
          opacity: 0.9,
          interactive: false,
        }).addTo(instance);
      }
    }

    youMarker = L.marker([state.position.lat, state.position.lon], {
      zIndexOffset: 1200,
      icon: L.divIcon({
        className: '',
        html: '<div class="youpin"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      }),
    })
      .addTo(instance)
      .bindTooltip('Ton adresse');

    instance.on('click', () => closeCard());
    // Fait apparaître ou disparaître les noms de commune selon le zoom.
    instance.on('zoomend', () => {
      if (lastState) drawZones(lastState);
    });

    return instance;
  }

  function closeCard(): void {
    selectedId = null;
    cardHost.replaceChildren();
    if (lastState) drawZones(lastState);
  }

  function drawWind(state: AppState): void {
    if (!map) return;
    for (const layer of windLayers) map.removeLayer(layer);
    windLayers = [];

    const context = plumeContext(state, Date.now());
    /*
     * Le couloir se trace avec le vent qui PORTE la fumée, et non celui du
     * sol : dessiner l'axe du vent de surface contredisait visuellement le
     * calcul des panaches.
     */
    const regime = regimeFor(context.regimes, '180m');
    if (!regime) return;

    if (regime.speed === null || regime.speed >= WIND_MIN_KMH) {
      const corridor = windCorridor(context.frame, regime.direction);

      /*
       * Deux enveloppes, et il faut les deux.
       *
       * La large tient compte de l'incertitude de direction du modèle météo,
       * qui est de l'ordre de 20 degrés. Ne tracer que la bande étroite laissait
       * croire que le vent est connu au degré près, alors qu'un tel écart suffit
       * à changer la liste des feux concernés.
       */
      windLayers.push(
        L.polygon(
          corridor.uncertainOutline.map((point) => [point[0], point[1]] as L.LatLngTuple),
          {
            color: COLOR_WIND,
            weight: 0,
            fillColor: COLOR_WIND,
            fillOpacity: 0.05,
            interactive: false,
          },
        ).addTo(map),
      );

      windLayers.push(
        L.polygon(corridor.outline.map((point) => [point[0], point[1]] as L.LatLngTuple), {
          color: COLOR_WIND,
          weight: 1.2,
          opacity: 0.5,
          dashArray: '6 6',
          fillColor: COLOR_WIND,
          fillOpacity: 0.09,
          interactive: false,
        }).addTo(map),
      );
      windLayers.push(
        L.polyline(
          [
            [state.position.lat, state.position.lon],
            [corridor.tip[0], corridor.tip[1]],
          ],
          {
            color: COLOR_WIND,
            weight: 2,
            opacity: 0.75,
            dashArray: '9 8',
            interactive: false,
          },
        ).addTo(map),
      );
    }

    /*
     * Flèche du vent. Le dessin de base a sa queue en haut et sa pointe au
     * centre ; le tourner de la direction du vent le met exactement dans le
     * sens réel de déplacement de l'air, queue du côté d'où il vient.
     */
    const radius = 110;
    const size = radius * 2;
    const arrow = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="overflow:visible" aria-hidden="true">
      <g transform="rotate(${regime.direction} ${radius} ${radius})" opacity=".95">
        <line x1="${radius}" y1="${radius - 98}" x2="${radius}" y2="${radius - 27}"
          stroke="${COLOR_WIND}" stroke-width="3.5" stroke-linecap="round" stroke-dasharray="9 7"/>
        <polygon points="${radius},${radius - 12} ${radius - 10},${radius - 33} ${radius + 10},${radius - 33}"
          fill="${COLOR_WIND}"/>
      </g></svg>`;

    windLayers.push(
      L.marker([state.position.lat, state.position.lon], {
        interactive: false,
        zIndexOffset: 400,
        icon: L.divIcon({
          className: '',
          html: arrow,
          iconSize: [size, size],
          iconAnchor: [radius, radius],
        }),
      }).addTo(map),
    );
  }

  /**
   * Surfaces brûlées d'EFFIS, sous les zones de feu.
   *
   * Dessinées en brun sombre, sans remplissage franc et sans interaction : ce
   * sont des cendres, pas des feux. Elles doivent se lire comme un décor et ne
   * jamais être confondues avec un foyer actif, sous peine de faire craindre une
   * fumée qui n'existe plus.
   *
   * Redessinées seulement quand la liste change. Elle ne bouge qu'une fois par
   * session, alors que les zones sont recalculées à chaque déplacement de carte.
   */
  function drawBurnt(state: AppState): void {
    if (!map || state.burnt === burntDrawn) return;
    for (const layer of burntLayers) map.removeLayer(layer);
    burntLayers = [];
    burntDrawn = state.burnt;

    for (const area of state.burnt) {
      if (area.rings.length === 0) continue;
      burntLayers.push(
        L.polygon(area.rings.map(toLatLngs), {
          color: COLOR_BURNT,
          weight: 1.5,
          opacity: 0.7,
          dashArray: '5 4',
          fillColor: COLOR_BURNT,
          fillOpacity: 0.14,
          interactive: false,
        }).addTo(map),
      );
    }
  }

  function drawZones(state: AppState): void {
    if (!map) return;
    drawBurnt(state);
    for (const layer of zoneLayers) map.removeLayer(layer);
    zoneLayers = [];

    const fires = selectNearbyFires(state, Date.now());
    const plumeById = new Map(fires.nearby.map((entry) => [entry.zone.id, entry.plume]));

    /*
     * Trajectoire de la fumée : un trait du feu jusqu'à l'adresse, pour chaque
     * feu réellement dans l'axe. Le couloir bleu ne peut pas montrer cela
     * seul, sa largeur étant fixe alors que le test tient compte de l'étendue
     * de chaque zone. Sans ce trait, la carte semblait contredire le texte.
     */
    for (const entry of fires.inPlume) {
      zoneLayers.push(
        L.polyline(
          [
            [entry.zone.lat, entry.zone.lon],
            [state.position.lat, state.position.lon],
          ],
          {
            color: COLOR_SMOKE,
            weight: 2.5,
            opacity: 0.85,
            dashArray: '10 7',
            interactive: false,
          },
        ).addTo(map),
      );
    }

    const zoom = map.getZoom();

    for (const zone of state.zones) {
      /*
       * Les torchères ne sont pas dessinées du tout.
       *
       * Elles l'étaient en gris pointillé, pour ne rien cacher. Mais une carte
       * d'incendies n'a pas à montrer des cheminées d'usine : cela encombre
       * l'écran sans rien apprendre à personne.
       *
       * La sécurité ne s'y perd pas. Une zone marquée `anomalous`, c'est-à-dire
       * qui déborde de l'empreinte habituelle de la source, repasse par
       * `isRealFire` et se dessine en rouge comme n'importe quel feu. Une
       * raffinerie qui brûle reste donc visible.
       */
      if (!isRealFire(zone)) continue;

      const hot = plumeById.get(zone.id)?.toward === true;
      const selected = selectedId === zone.id;
      const weak = isWeakDetection(zone);

      /*
       * Un signal faible reste dessiné, mais discrètement.
       *
       * Il ne compte pas, n'est pas listé et ne déclenche aucune consigne. Le
       * garder visible tient à une raison simple : un incendie naissant
       * commence forcément petit, et l'effacer serait se taire au pire moment.
       */
      const style: L.PathOptions = weak
        ? {
            color: COLOR_FIRE,
            weight: 1,
            opacity: 0.45,
            fillColor: COLOR_FIRE,
            fillOpacity: 0.06,
          }
        : {
            color: hot ? COLOR_FIRE_HOT : COLOR_FIRE,
            weight: selected ? 4 : hot ? 3.5 : 2.5,
            opacity: 0.95,
            fillColor: COLOR_FIRE,
            fillOpacity: selected ? 0.35 : hot ? 0.3 : 0.22,
          };

      /*
       * Un polygone à plusieurs anneaux quand on a le contour des empreintes,
       * un cercle sinon. Leaflet accepte un tableau d'anneaux, ce qui permet
       * de dessiner un foyer discontinu tel qu'il a été observé, sans relier
       * ses îlots par une enveloppe qui inventerait de la surface.
       *
       * smoothFactor reste bas : le contour suit une grille de 375 m, et le
       * lisser davantage reviendrait à redessiner ce que le satellite a mesuré.
       */
      const shape =
        zone.rings.length > 0
          ? L.polygon(zone.rings.map(toLatLngs), { ...style, smoothFactor: 0.5 })
          : L.circle([zone.lat, zone.lon], { ...style, radius: zone.radiusKm * 1000 });

      shape.addTo(map);
      shape.on('click', (event) => {
        L.DomEvent.stopPropagation(event);
        openCard(state, zone.id);
      });
      zoneLayers.push(shape);

      // Les noms ne s'affichent qu'en zoom rapproché : à l'échelle de la
      // France ils se chevauchaient tous et rendaient la carte illisible.
      // Pas de nom sur un signal faible : cela lui donnerait l'importance
      // d'un incendie déclaré.
      const label = weak ? undefined : zone.place?.label;
      if (label && zoom >= MAP_LABEL_MIN_ZOOM) {
        zoneLayers.push(
          L.marker([zone.lat, zone.lon], {
            interactive: false,
            zIndexOffset: 300,
            icon: L.divIcon({
              className: '',
              html: `<div class="zone-label">${label.replace(/[<>&]/g, '')}</div>`,
              iconSize: [0, 0],
              iconAnchor: [0, -14],
            }),
          }).addTo(map),
        );
      }
    }
  }

  function zoneDetails(
    zone: FireZone,
    plume: PlumeAssessment | null,
    state: AppState,
    now: number,
  ): void {
    const distance = distanceKm(state.position.lat, state.position.lon, zone.lat, zone.lon);

    render(
      cardHost,
      html`
        <div class="zone-card" role="dialog" aria-label="Détails du feu">
          <button type="button" class="close" data-close="1" aria-label="Fermer">
            ×
          </button>
          <h2>${zone.place?.label ?? 'Feu'}</h2>
          <p class="sub">
            ${zone.place?.department
              ? `Département ${zone.place.department}`
              : 'Localité inconnue'}
          </p>

          <dl class="kv">
            <div><dt>Distance de chez toi</dt><dd>${Math.round(distance)} km</dd></div>
            <div>
              <dt>Direction</dt>
              <dd>${plume ? directionTo(plume.bearing) : '-'}</dd>
            </div>
            <div>
              <dt>Étendue détectée</dt>
              <dd>environ ${Math.round(zone.radiusKm * 2)} km</dd>
            </div>
            <div>
              <dt>Taches détectées</dt>
              <dd>${zone.pixelCount}</dd>
            </div>
            ${zone.confidence !== null
              ? html`<div>
                  <dt>Confiance satellite</dt>
                  <dd>${Math.round(zone.confidence)} sur 100</dd>
                </div>`
              : ''}
            ${zone.maxFrp !== null
              ? html`<div>
                  <dt>Puissance dégagée</dt>
                  <dd>${Math.round(zone.maxFrp)} MW</dd>
                </div>`
              : ''}
            ${zone.observedAt !== null
              ? html`<div>
                  <dt>Dernier passage satellite</dt>
                  <dd>${formatClock(zone.observedAt, now)}</dd>
                </div>`
              : ''}
            <div>
              <dt>Vu par satellite</dt>
              <dd>${dayLabel(state.fireDay, now)}</dd>
            </div>
            ${plume?.toward && plume.travelHours !== null
              ? html`
                  <div>
                    <dt>Trajet de la fumée</dt>
                    <dd>environ ${durationWords(plume.travelHours)}</dd>
                  </div>
                  <div>
                    <dt>Vent qui la porte</dt>
                    <dd>
                      ${Math.round(plume.windSpeed ?? 0)} km/h vers ${plume.level.label}
                    </dd>
                  </div>
                  <div>
                    <dt>Vent stable depuis</dt>
                    <dd>${formatClock(plume.steadySince, now)}</dd>
                  </div>
                `
              : ''}
          </dl>

          ${plume
            ? plume.toward
              ? html`
                  <p class="notice" data-kind="warn">
                    <strong>Ce feu est au vent de chez toi.</strong> Il se trouve
                    ${directionTo(plume.bearing)}, et le vent porte sa fumée dans ta
                    direction.
                    ${plume.arrival != null
                      ? html`Le vent a tourné à
                          <strong>${formatClock(plume.steadySince, now)}</strong> : la
                          fumée n'a pas encore fait le trajet, elle devrait te toucher
                          vers <strong>${formatClock(plume.arrival, now)}</strong>.`
                      : html`Le vent souffle ainsi depuis
                          ${durationWords(plume.hoursSinceShift)}, plus longtemps que le
                          trajet : sa fumée est déjà sur toi.`}
                  </p>
                `
              : html`
                  <p class="notice" data-kind="calm">
                    Ce feu est ${directionTo(plume.bearing)}, mais le vent ne porte pas
                    sa fumée vers toi. Si le vent tourne, ça change.
                  </p>
                `
            : ''}

          ${isWeakDetection(zone)
            ? html`
                <p class="notice" data-kind="note">
                  <strong>Signal faible.</strong>
                  ${zone.confidence !== null
                    ? html`Confiance de ${Math.round(zone.confidence)} sur 100${zone.maxFrp !==
                      null
                        ? `, ${Math.round(zone.maxFrp)} MW dégagés`
                        : ''}.`
                    : html`Seulement ${zone.pixelCount} taches, là où un grand incendie en
                      donne plusieurs centaines.`}
                  Ce peut être un brûlage agricole, un feu déjà éteint, ou une source
                  ponctuelle.
                </p>
              `
            : ''}

          <p class="notice" data-kind="note">
            <strong>Tu vois un feu ? Appelle le 112.</strong> Cette carte vient de
            satellites et ne remplace ni les pompiers ni les consignes de ta préfecture.
          </p>
          <p class="hint">
            L'étendue indiquée est celle des taches détectées à la résolution
            d'affichage du satellite, et non le périmètre du feu. Elle surestime souvent
            un petit foyer. Les périmètres réels sont établis par les SDIS et ne sont pas
            publiés en flux ouvert.
          </p>
        </div>
      `,
    );

    const closeButton = cardHost.querySelector<HTMLButtonElement>('[data-close]');
    closeButton?.addEventListener('click', () => closeCard());
    closeButton?.focus();
  }

  function openCard(state: AppState, zoneId: string): void {
    const zone = state.zones.find((candidate) => candidate.id === zoneId);
    if (!zone || !map) {
      closeCard();
      return;
    }

    selectedId = zoneId;
    drawZones(state);

    const fires = selectNearbyFires(state, Date.now());
    const plume = fires.nearby.find((entry) => entry.zone.id === zoneId)?.plume ?? null;

    zoneDetails(zone, plume, state, Date.now());
    map.setView([zone.lat, zone.lon], Math.max(map.getZoom(), 9), { animate: true });
  }

  return {
    activate(state) {
      lastState = state;
      if (!map) {
        map = build(state);
        drawWind(state);
        drawZones(state);
      }
      // Leaflet a besoin de recalculer sa taille après l'affichage de l'onglet.
      setTimeout(() => map?.invalidateSize(), 60);
    },

    render(state) {
      lastState = state;
      if (!map) return;
      youMarker?.setLatLng([state.position.lat, state.position.lon]);
      drawWind(state);
      drawZones(state);
    },

    focusZone(state, zoneId) {
      lastState = state;
      if (!map) {
        map = build(state);
        drawWind(state);
      }
      setTimeout(() => {
        map?.invalidateSize();
        openCard(state, zoneId);
      }, 80);
    },
  };
}
